(() => {
  const DRAWER_SEL = '#aov-cart-drawer';
  const SUB_ITEM_SEL = '.AOV-CartDrawer__SubscriptionItem';
  const QTY_WRAP_SEL = '.AOV-CartDrawer-ItemQuantity';
  const INPUT_SEL = 'input[type="number"]';
  const BTN_PLUS_SEL = 'button:last-of-type';

  // Default cap for subscription items
  const DEFAULT_SUB_MAX_QTY = 2;

  // Performance Pack specific cap (subscription only)
  const PERF_PACK_PRODUCT_ID = 10448039706961; // <-- keep / update if needed
  const PERF_PACK_SUB_MAX_QTY = 1;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function changeLine(line, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line, quantity })
    });
  }

  function lockInputToButtonsOnly(input) {
    if (!input) return;
    input.readOnly = true;
    input.setAttribute('inputmode', 'none');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-readonly', 'true');
    input.style.userSelect = 'none';
  }

  function getMaxForSubItem(subItem) {
    const pid = parseInt(subItem.getAttribute('data-product-id') || '0', 10);
    if (pid === PERF_PACK_PRODUCT_ID) return PERF_PACK_SUB_MAX_QTY;
    return DEFAULT_SUB_MAX_QTY;
  }

  function syncSubItemQtyUI(subItem) {
    const wrap = subItem.querySelector(QTY_WRAP_SEL);
    const input = wrap?.querySelector(INPUT_SEL);
    const plus = wrap?.querySelector(BTN_PLUS_SEL);
    if (!input) return;

    const maxQty = getMaxForSubItem(subItem);

    // Lock manual input (buttons only)
    lockInputToButtonsOnly(input);

    input.setAttribute('max', String(maxQty));
    input.setAttribute('min', '1');

    const qty = parseInt(input.value || '1', 10) || 1;

    // disable "+" at cap
    if (plus) {
      const atCap = qty >= maxQty;
      plus.disabled = atCap;
      plus.style.opacity = atCap ? '0.4' : '';
      plus.style.pointerEvents = atCap ? 'none' : '';
      if (atCap) plus.setAttribute('aria-disabled', 'true');
      else plus.removeAttribute('aria-disabled');
    }
  }

  function syncAllUI() {
    const drawer = document.querySelector(DRAWER_SEL);
    if (!drawer) return;
    drawer.querySelectorAll(SUB_ITEM_SEL).forEach(syncSubItemQtyUI);
  }

  // HARDEN: if any subscription line in Shopify cart exceeds its max, force it back
  let isFixing = false;
  async function enforceSubQtyServerSide() {
    if (isFixing) return;
    isFixing = true;

    try {
      const cart = await fetchCart();

      const fixes = [];
      cart.items.forEach((item, idx) => {
        const isSub = !!item.selling_plan_allocation; // Appstle = native selling plans
        if (!isSub) return;

        const maxQty = (item.product_id === PERF_PACK_PRODUCT_ID)
          ? PERF_PACK_SUB_MAX_QTY
          : DEFAULT_SUB_MAX_QTY;

        if (item.quantity > maxQty) {
          fixes.push({ line: idx + 1, quantity: maxQty });
        }
      });

      for (const f of fixes) {
        await changeLine(f.line, f.quantity);
      }

      if (fixes.length) {
        setTimeout(syncAllUI, 250);
      }
    } catch (e) {
      // silent fail
    } finally {
      isFixing = false;
    }
  }

  // Block "+" clicks in capture phase (beats AOV handlers)
  document.addEventListener('click', (e) => {
    const subItem = e.target.closest?.(SUB_ITEM_SEL);
    if (!subItem) return;

    const wrap = e.target.closest?.(QTY_WRAP_SEL) || subItem.querySelector(QTY_WRAP_SEL);
    const input = wrap?.querySelector(INPUT_SEL);
    const plus = wrap?.querySelector(BTN_PLUS_SEL);
    if (!wrap || !input || !plus) return;

    // Ensure input is locked even if AOV re-rendered
    lockInputToButtonsOnly(input);

    const isPlus = e.target === plus || plus.contains(e.target);
    if (!isPlus) return;

    const maxQty = getMaxForSubItem(subItem);
    const qty = parseInt(input.value || '1', 10) || 1;

    if (qty >= maxQty) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      syncSubItemQtyUI(subItem);
    }
  }, true);

  // Prevent typing/pasting/wheel on subscription qty inputs (extra hardening)
  document.addEventListener('keydown', (e) => {
    const input = e.target.closest?.(`${DRAWER_SEL} ${SUB_ITEM_SEL} ${QTY_WRAP_SEL} ${INPUT_SEL}`);
    if (!input) return;
    e.preventDefault();
  }, true);

  document.addEventListener('paste', (e) => {
    const input = e.target.closest?.(`${DRAWER_SEL} ${SUB_ITEM_SEL} ${QTY_WRAP_SEL} ${INPUT_SEL}`);
    if (!input) return;
    e.preventDefault();
  }, true);

  document.addEventListener('wheel', (e) => {
    const input = e.target.closest?.(`${DRAWER_SEL} ${SUB_ITEM_SEL} ${QTY_WRAP_SEL} ${INPUT_SEL}`);
    if (!input) return;
    e.preventDefault();
  }, { passive: false, capture: true });

  // Re-apply on drawer re-render
  function observeDrawer() {
    const drawer = document.querySelector(DRAWER_SEL);
    if (!drawer) return;

    const mo = new MutationObserver(() => {
      syncAllUI();
      enforceSubQtyServerSide();
    });
    mo.observe(drawer, { childList: true, subtree: true });

    syncAllUI();
    enforceSubQtyServerSide();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeDrawer);
  } else {
    observeDrawer();
  }
})();